import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    input,
    output,
    viewChild,
} from '@angular/core';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ViewFilterKey } from '@app/shared/models/view-config.interface';
import { DATE_FILTER_PLACEHOLDER } from '@app/shared/services/view-configs/filter-key-creator';

/**
 * The filter control shown when a column header's filter icon is clicked.
 *
 * Purely presentational - it neither reads nor writes the SearchStore, so the
 * debounce and the "empty value removes the filter" rule live in one place.
 */
@Component({
    selector: 'app-column-filter-popover',
    templateUrl: './column-filter-popover.component.html',
    styleUrls: ['./column-filter-popover.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormField,
        MatSuffix,
        MatInput,
        MatIcon,
        MatIconButton,
        MatRadioGroup,
        MatRadioButton,
        MatTooltip,
        TranslateModule,
    ],
})
export class ColumnFilterPopoverComponent implements AfterViewInit {
    filterKey = input.required<ViewFilterKey>();
    /** Currently applied value, or `''` when this column is not filtered. */
    value = input<string>('');

    /** Emits the new value; `''` means "remove this filter". */
    valueChange = output<string>();
    closed = output<void>();

    protected readonly datePlaceholder = DATE_FILTER_PLACEHOLDER;

    private textInput =
        viewChild<ElementRef<HTMLInputElement>>('textFilterInput');

    protected type = computed(() => this.filterKey().type ?? 'text');
    protected isTextual = computed(
        () => this.type() === 'text' || this.type() === 'date'
    );

    ngAfterViewInit(): void {
        // The overlay creates this component on open, so focusing here puts the
        // caret in the input without an extra click.
        this.textInput()?.nativeElement.focus();
    }

    protected onTextInput(event: Event): void {
        this.valueChange.emit((event.target as HTMLInputElement).value);
    }

    /** Radio groups close on pick - there is nothing else to do in the popover. */
    protected onOptionSelected(optionValue: string): void {
        this.valueChange.emit(optionValue);
        this.closed.emit();
    }

    /** Clears the text input but stays open so the user can type a new value. */
    protected clear(): void {
        const input = this.textInput()?.nativeElement;
        if (input) {
            input.value = '';
            input.focus();
        }
        this.valueChange.emit('');
    }

    protected close(): void {
        this.closed.emit();
    }
}
